import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY = {
  title: "",
  excerpt: "",
  content: "",
  category: "Actualité",
  cover_image: "",
  published: true,
};

export default function AdminArticles() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () =>
    api.get("/admin/articles").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      title: a.title,
      excerpt: a.excerpt,
      content: a.content,
      category: a.category || "Actualité",
      cover_image: a.cover_image || "",
      published: a.published,
    });
  };
  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/articles", form);
        toast.success("Article créé.");
      } else {
        await api.put(`/admin/articles/${editing}`, form);
        toast.success("Article mis à jour.");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Article supprimé.");
      load();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-10">
        <div>
          <span className="font-overline text-[#B8935A]">Back-office</span>
          <h1 className="font-serif text-3xl text-[#0F172A] mt-2">Actualités</h1>
        </div>
        <button onClick={openNew} data-testid="admin-articles-new" className="gpi-btn-primary">
          <Plus size={16} /> Nouvel article
        </button>
      </div>

      <div className="bg-white border border-[#E2E8F0]">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] text-left text-[#64748B] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3">Titre</th>
              <th className="px-5 py-3">Catégorie</th>
              <th className="px-5 py-3">Statut</th>
              <th className="px-5 py-3">Mis à jour</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-[#E2E8F0]" data-testid={`admin-article-row-${a.id}`}>
                <td className="px-5 py-4 font-medium text-[#0F172A]">{a.title}</td>
                <td className="px-5 py-4 text-[#64748B]">{a.category}</td>
                <td className="px-5 py-4">
                  <span
                    className={`px-2 py-1 text-xs ${
                      a.published ? "bg-[#EFE8DC] text-[#0F172A]" : "bg-[#FEE2E2] text-[#991B1B]"
                    }`}
                  >
                    {a.published ? "Publié" : "Brouillon"}
                  </span>
                </td>
                <td className="px-5 py-4 text-[#64748B]">
                  {new Date(a.updated_at).toLocaleDateString("fr-FR")}
                </td>
                <td className="px-5 py-4 text-right">
                  <button
                    onClick={() => openEdit(a)}
                    data-testid={`admin-article-edit-${a.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[#0F172A] hover:bg-[#EFE8DC] mr-1"
                  >
                    <Pencil size={14} /> Éditer
                  </button>
                  <button
                    onClick={() => remove(a.id)}
                    data-testid={`admin-article-delete-${a.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[#991B1B] hover:bg-[#FEE2E2]"
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center text-[#64748B]">
                  Aucun article. Cliquez sur "Nouvel article".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start lg:items-center justify-center p-4 overflow-auto">
          <form
            onSubmit={save}
            className="bg-white w-full max-w-3xl border border-[#E2E8F0] my-8"
            data-testid="admin-article-form"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
              <h2 className="font-serif text-xl text-[#0F172A]">
                {editing === "new" ? "Nouvel article" : "Modifier l'article"}
              </h2>
              <button type="button" onClick={close} className="text-[#64748B] hover:text-[#0F172A]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <Input label="Titre" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="form-title" />
              <Input label="Catégorie" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="form-category" />
              <Input label="Image de couverture (URL)" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testid="form-cover" />
              <Input label="Extrait" required value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} testid="form-excerpt" textarea rows={2} />
              <Input label="Contenu (HTML)" required value={form.content} onChange={(v) => setForm({ ...form, content: v })} testid="form-content" textarea rows={10} />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="form-published"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                />
                Publié
              </label>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-[#E2E8F0] bg-[#F9F8F6]">
              <button type="button" onClick={close} className="gpi-btn-outline">
                Annuler
              </button>
              <button type="submit" className="gpi-btn-primary" data-testid="admin-article-save">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, required, testid, textarea, rows = 3 }) {
  const props = {
    "data-testid": testid,
    value,
    onChange: (e) => onChange(e.target.value),
    required,
    className:
      "w-full px-4 py-2.5 border border-[#E2E8F0] bg-[#F9F8F6] focus:border-[#0F172A] focus:bg-white outline-none transition-colors text-sm",
  };
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-2">
        {label} {required && <span className="text-[#B8935A]">*</span>}
      </span>
      {textarea ? <textarea rows={rows} {...props} /> : <input {...props} />}
    </label>
  );
}
